import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiPlus, FiArrowRight, FiInbox } from "react-icons/fi";
import { PanelCard } from "./dashboardShared";
import { UserAvatar } from "../UserAvatar";
import { usePermissions } from "../../Auth/PermissionsContext";
import { useRealtimeEvent } from "../../Realtime/RealtimeProvider";
import { formatRelativeTimeFr } from "../../Helper/dates";
import { TicketCreateModal } from "../Tickets/TicketCreateModal";
import { TicketDetailModal } from "../Tickets/TicketDetailModal";
import { ticketsClient, type Ticket, type TicketCategory } from "../../Pages/Tickets/ticketsClient";
import { orderDashboardTickets } from "../../Pages/Tickets/ticketUi";

/** One compact ticket row (avatar of the creator + title + relative time). Opens the read-only detail
 *  modal on click so the admin stays on the dashboard. Mirrors LinkedTicketsBanner's row. Tickets
 *  assigned to ME carry a discreet sky dot (no verbose section titles : space is scarce here). */
const TicketRow: React.FC<{ ticket: Ticket & { mine: boolean }; onOpen: (id: string) => void }> = ({ ticket, onOpen }) => (
	<button
		type="button"
		onClick={() => onOpen(ticket.id)}
		className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition hover:bg-slate-50"
		title={ticket.mine ? `${ticket.title} : assigné à vous` : ticket.title}
	>
		<UserAvatar
			name={ticket.createdBy?.name ?? ticket.createdByEmail}
			id={ticket.createdBy?.id ?? undefined}
			avatarKey={ticket.createdBy?.avatarKey}
			pictureUrl={ticket.createdBy?.pictureUrl}
			sizeClass="h-6 w-6"
			textClass="text-[9px]"
		/>
		<span className="min-w-0 flex-1">
			<span className="flex items-center gap-1.5">
				{ticket.mine && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500" aria-label="Assigné à vous" />}
				<span className="min-w-0 truncate text-xs font-semibold text-slate-800">{ticket.title}</span>
			</span>
			<span className="block truncate text-[10px] text-slate-400">{formatRelativeTimeFr(ticket.updatedAt)}</span>
		</span>
	</button>
);

/**
 * Compact dashboard tickets column : ONE flat list: the current admin's assigned open tickets FIRST
 * (sky dot), then the most recent open tickets of the whole team (deduped). No verbose section
 * titles (space is scarce); a « Créer » button and a « Voir tout » link to the full Tickets page.
 * Clicking a row opens the read-only detail modal without leaving the dashboard. Live via the
 * realtime « ticket » event. Hidden entirely for admins without tickets read access.
 */
export const TicketsPanel: React.FC = () => {
	const { can } = usePermissions();
	const canRead = can("feature:tickets:read");
	const canWrite = can("feature:tickets:write");

	const [mine, setMine] = useState<Ticket[]>([]);
	const [recent, setRecent] = useState<Ticket[]>([]);
	const [categories, setCategories] = useState<TicketCategory[]>([]);
	const [showCreate, setShowCreate] = useState(false);
	const [openTicketId, setOpenTicketId] = useState<string | null>(null);

	const categoryLabel = useMemo(() => {
		const map = new Map(categories.map((c) => [c.key, c.label]));
		return (key: string) => map.get(key) ?? key;
	}, [categories]);

	const load = useCallback(async () => {
		if (!canRead) return;
		try {
			const [mineRes, recentRes] = await Promise.all([
				ticketsClient.list({ mine: true, status: "open", pageSize: 4 }),
				ticketsClient.list({ status: "open", pageSize: 5 })
			]);
			setMine(mineRes.items);
			setRecent(recentRes.items);
		} catch {
			/* transient : keep the last good lists */
		}
	}, [canRead]);

	useEffect(() => {
		load();
		if (canRead) ticketsClient.categories().then(setCategories).catch(() => setCategories([]));
	}, [load, canRead]);

	useRealtimeEvent("ticket", () => load());

	if (!canRead) return null;

	const action = (
		<div className="flex items-center gap-1.5">
			{canWrite && (
				<button
					type="button"
					onClick={() => setShowCreate(true)}
					className="inline-flex items-center gap-1 rounded-lg bg-[#005CA9] px-2 py-1 text-[11px] font-semibold text-white transition hover:bg-[#004a87]"
				>
					<FiPlus className="h-3 w-3" /> Créer
				</button>
			)}
			<Link
				to="/tickets"
				className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 transition hover:bg-slate-50"
			>
				Voir tout <FiArrowRight className="h-3 w-3" />
			</Link>
		</div>
	);

	const tickets = orderDashboardTickets(mine, recent);

	return (
		<>
			<PanelCard title="Tickets" action={action} className="h-full" bodyClassName="p-3">
				{tickets.length === 0 ? (
					<div className="flex flex-col items-center gap-2 px-2 py-8 text-center text-slate-400">
						<FiInbox className="h-6 w-6" />
						<p className="text-xs">Aucun ticket ouvert.</p>
						{canWrite && (
							<button type="button" onClick={() => setShowCreate(true)} className="text-[11px] font-semibold text-[#005CA9] hover:underline">
								Créer le premier ticket
							</button>
						)}
					</div>
				) : (
					<div className="space-y-0.5">
						{tickets.map((t) => <TicketRow key={t.id} ticket={t} onOpen={setOpenTicketId} />)}
					</div>
				)}
			</PanelCard>

			<TicketCreateModal
				open={showCreate}
				onClose={() => setShowCreate(false)}
				onCreated={() => load()}
			/>
			<TicketDetailModal ticketId={openTicketId} categoryLabel={categoryLabel} onClose={() => setOpenTicketId(null)} />
		</>
	);
};

export default TicketsPanel;
